"use client";

import { Check, Loader2, X } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../../lib/utils";

export type ButtonVariant = "primary" | "secondary" | "borderless";
export type ButtonTone = "neutral" | "danger";
export type ButtonSize = "md" | "sm" | "xs";
export type ButtonState = "idle" | "loading" | "success" | "error";

/** Every Button prop except the async status ones — for wrappers that don't pass status through. */
export interface ButtonBaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  /**
   * Toggled-on look, for a button that holds a state — an engaged filter.
   * Paints the pressed colours, without the 1px settle. An open menu or
   * popover trigger gets the same look from `aria-expanded` on its own.
   */
  active?: boolean;
  /** Leading icon. Hidden while `state` shows a status icon in its place. */
  icon?: ReactNode;
}

export interface ButtonProps extends ButtonBaseProps {
  /**
   * Optional status. `loading` swaps the leading icon for a spinner, the label
   * for `loadingText`, and disables the button; `success` and `error` do the
   * same with a check or a cross. Leaving it undefined renders a plain button.
   */
  state?: ButtonState;
  loadingText?: ReactNode;
  successText?: ReactNode;
  errorText?: ReactNode;
}

/**
 * How every member of the system moves and fades, shared with `IconButton`:
 * fill, edge and the 1px press settle on the `v2-control` timing, snapping to
 * `v2-press` while held; a disabled button fades to 42% and stops taking the
 * pointer, which is what keeps hover and press from firing on it.
 *
 * Hover and press are plain `hover:` / `active:` rules, so a call site that
 * really needs its own state colour overrides them through `className` the
 * ordinary way — tailwind-merge drops the base rule — and the same classes work
 * on an `<a>`.
 */
export const BUTTON_INTERACTION_CLASS = cn(
  "relative inline-flex cursor-pointer select-none items-center justify-center border border-solid",
  "transition-[background-color,border-color,transform] duration-v2-control ease-v2-control active:duration-v2-press",
  "active:translate-y-px",
  "disabled:pointer-events-none disabled:opacity-[0.42]",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--v2-button-focus)]",
);

/**
 * `[variant][tone]` colours, shared with `IconButton`. Each type steps one way
 * on hover and the other on press; `active` (`data-active`) and an open
 * overlay (`aria-expanded`, which every menu and popover trigger sets) hold the
 * press colours.
 */
export const BUTTON_COLOR_CLASS: Record<ButtonVariant, Record<ButtonTone, string>> = {
  primary: {
    neutral: cn(
      "text-[color:var(--v2-button-on-ink)]",
      "border-[color:var(--v2-button-ink)] bg-[color:var(--v2-button-ink)]",
      "hover:border-[color:var(--v2-button-ink-hover)] hover:bg-[color:var(--v2-button-ink-hover)]",
      "active:border-[color:var(--v2-button-ink-active)] active:bg-[color:var(--v2-button-ink-active)]",
      "data-[active=true]:border-[color:var(--v2-button-ink-active)] data-[active=true]:bg-[color:var(--v2-button-ink-active)] aria-expanded:border-[color:var(--v2-button-ink-active)] aria-expanded:bg-[color:var(--v2-button-ink-active)]",
    ),
    danger: cn(
      "text-[color:var(--v2-button-on-danger)]",
      "border-[color:var(--v2-button-danger)] bg-[color:var(--v2-button-danger)]",
      "hover:border-[color:var(--v2-button-danger-hover)] hover:bg-[color:var(--v2-button-danger-hover)]",
      "active:border-[color:var(--v2-button-danger-active)] active:bg-[color:var(--v2-button-danger-active)]",
      "data-[active=true]:border-[color:var(--v2-button-danger-active)] data-[active=true]:bg-[color:var(--v2-button-danger-active)] aria-expanded:border-[color:var(--v2-button-danger-active)] aria-expanded:bg-[color:var(--v2-button-danger-active)]",
    ),
  },
  secondary: {
    neutral: cn(
      "text-[color:var(--v2-button-label)]",
      "border-[color:var(--v2-button-edge)] bg-[color:var(--v2-button-surface)]",
      "hover:border-[color:var(--v2-button-edge-hover)] hover:bg-[color:var(--v2-button-wash)]",
      "active:border-[color:var(--v2-button-edge-active)] active:bg-[color:var(--v2-button-wash-active)]",
      "data-[active=true]:border-[color:var(--v2-button-edge-active)] data-[active=true]:bg-[color:var(--v2-button-wash-active)] aria-expanded:border-[color:var(--v2-button-edge-active)] aria-expanded:bg-[color:var(--v2-button-wash-active)]",
    ),
    danger: cn(
      "text-[color:var(--v2-button-danger-label)]",
      "border-[color:var(--v2-button-danger-edge)] bg-[color:var(--v2-button-surface)]",
      "hover:border-[color:var(--v2-button-danger-edge-hover)] hover:bg-[color:var(--v2-button-danger-wash)]",
      "active:border-[color:var(--v2-button-danger-edge-active)] active:bg-[color:var(--v2-button-danger-wash-active)] active:text-[color:var(--v2-button-danger-label-active)]",
      "data-[active=true]:border-[color:var(--v2-button-danger-edge-active)] data-[active=true]:bg-[color:var(--v2-button-danger-wash-active)] aria-expanded:border-[color:var(--v2-button-danger-edge-active)] aria-expanded:bg-[color:var(--v2-button-danger-wash-active)]",
    ),
  },
  borderless: {
    neutral: cn(
      "text-[color:var(--v2-button-label)]",
      "border-transparent bg-transparent",
      "hover:bg-[color:var(--v2-button-wash)]",
      "active:bg-[color:var(--v2-button-wash-active)]",
      "data-[active=true]:bg-[color:var(--v2-button-wash-active)] aria-expanded:bg-[color:var(--v2-button-wash-active)]",
    ),
    danger: cn(
      "text-[color:var(--v2-button-danger-label)]",
      "border-transparent bg-transparent",
      "hover:bg-[color:var(--v2-button-danger-wash-soft)]",
      "active:bg-[color:var(--v2-button-danger-wash-active)] active:text-[color:var(--v2-button-danger-label-active)]",
      "data-[active=true]:bg-[color:var(--v2-button-danger-wash-active)] aria-expanded:bg-[color:var(--v2-button-danger-wash-active)]",
    ),
  },
};

/**
 * Every dimension steps once per size, in the same direction: 13 / 12.5 / 12px
 * type, 8 / 6 / 4px above and below, 14 / 12 / 10px either side, a 15 / 14 /
 * 13px icon and an 8 / 7 / 6px gap. `md` is the spec's "Default".
 *
 * The line-height sits after the font size on purpose: tailwind-merge drops a
 * `leading-*` that precedes a font-size class, so it cannot live in the base.
 */
const SIZE_CLASS: Record<ButtonSize, string> = {
  md: "gap-2 px-3.5 py-2 text-[13px] leading-[1.5] [&_svg]:size-[15px]",
  sm: "gap-[7px] px-3 py-1.5 text-[12.5px] leading-[1.5] [&_svg]:size-3.5",
  xs: "gap-1.5 px-2.5 py-1 text-[12px] leading-[1.5] [&_svg]:size-[13px]",
};

/**
 * The label-bearing extras on top of the shared interaction classes. The 1.5
 * line-height lands the heights on the spec's ladder (37.5 / 32.75 / 28px) —
 * label box plus padding plus the 1px edge, which borderless holds transparent
 * so it measures the same. The spec's 20px radius is already a full pill at
 * every height on that ladder; `rounded-full` draws the same shape and keeps
 * it a pill if a call site ever makes one taller.
 */
const LABEL_CLASS = "rounded-full whitespace-nowrap font-sans [&_svg]:shrink-0 [&_svg]:[stroke-width:1.8]";

/**
 * Every variant × tone × size string, merged once at module load. A Button
 * render is then a lookup, and only a call-site `className` pays for a merge.
 */
const PRESET_CLASS = Object.fromEntries(
  (Object.keys(BUTTON_COLOR_CLASS) as ButtonVariant[]).flatMap((variant) =>
    (["neutral", "danger"] as const).flatMap((tone) =>
      (Object.keys(SIZE_CLASS) as ButtonSize[]).map((size) => [
        `${variant}.${tone}.${size}`,
        cn(
          BUTTON_INTERACTION_CLASS,
          LABEL_CLASS,
          // Primary is the only filled type and the only one set at 500.
          variant === "primary" ? "font-medium" : "font-normal",
          BUTTON_COLOR_CLASS[variant][tone],
          SIZE_CLASS[size],
        ),
      ]),
    ),
  ),
) as Record<`${ButtonVariant}.${ButtonTone}.${ButtonSize}`, string>;

/**
 * The full Button class string — for a non-`<button>` element (a link) that
 * has to look like one, and used by `Button` itself.
 */
export function buttonClassName({
  variant = "primary",
  tone = "neutral",
  size = "md",
  className,
}: {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  className?: string;
} = {}) {
  const preset = PRESET_CLASS[`${variant}.${tone}.${size}`];
  return className ? cn(preset, className) : preset;
}

const STATE_ICON: Record<Exclude<ButtonState, "idle">, ReactNode> = {
  loading: <Loader2 aria-hidden className="animate-spin" />,
  success: <Check aria-hidden />,
  error: <X aria-hidden />,
};

/**
 * The Strive button system: three types, two tones, three sizes.
 *
 * - `primary` — the main action, one per footer. The only filled button and the
 *   only one set at weight 500.
 * - `secondary` — the supporting action beside it. White pill, hairline edge.
 * - `borderless` — secondary with its edge taken away, for dismissive actions
 *   that should not compete.
 *
 * Every type comes in a `neutral` (ink) and a `danger` (red) tone, and steps
 * down from `md` (the spec's "Default") through `sm` and `xs`. Colours live in
 * `--v2-button-*` (v2-tokens.css) so dark mode is a token swap.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    tone = "neutral",
    size = "md",
    active,
    icon,
    state,
    loadingText = "Loading",
    successText = "Done",
    errorText = "Try again",
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const status = state && state !== "idle" ? state : undefined;
  const label = status
    ? { loading: loadingText, success: successText, error: errorText }[status]
    : children;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || status === "loading"}
      aria-busy={status === "loading" || undefined}
      data-active={active ? "true" : undefined}
      data-variant={variant}
      data-tone={tone}
      className={buttonClassName({ variant, tone, size, className })}
      {...rest}
    >
      {status ? STATE_ICON[status] : icon}
      {state !== undefined ? <span aria-live="polite">{label}</span> : label}
    </button>
  );
});
